import {
  OmniCardChrome,
  OmniCardHeader,
  type OmniCardPagination,
  type OmniCardStaging,
} from "@/components/artifacts/OmniCard.tsx";
import { OmniDismissButton } from "@/components/artifacts/OmniDismissButton.tsx";
import { ConnectorLogo } from "@/components/connectors/ConnectorLogo.tsx";
import { Button } from "@/components/ui/button.tsx";
import type { StagedDecision } from "@/shared/artifactDecisions.ts";
import type { ArtifactAction, OAuthConnectionArtifact } from "@/types/artifacts.ts";
import { ArrowUpRight, Check, Globe, LockKeyhole } from "lucide-react";
import type { ReactNode, Ref } from "react";

/** The sign-in handoff lives in the composer, with no separate detail panel. */
export const BrowserSignInBiscuit = ({
  artifact,
  actions,
  onAction,
  disabled,
  pagination,
  staging,
  onDismiss,
  followUp,
  browserControlRef,
}: {
  artifact: OAuthConnectionArtifact;
  actions: ArtifactAction[];
  onAction: (action: ArtifactAction) => void;
  disabled?: boolean;
  pagination?: OmniCardPagination;
  staging?: OmniCardStaging & { decision?: StagedDecision };
  onDismiss?: () => void;
  followUp?: ReactNode;
  browserControlRef?: Ref<HTMLDivElement>;
}) => (
  <OmniCardChrome onDismiss={onDismiss}>
    {pagination && (
      <OmniCardHeader
        pagination={pagination}
        staging={staging}
        onDismiss={onDismiss}
        statsigPrefix="artifact-biscuit"
      />
    )}
    <div className="flex items-start gap-3 px-1 pt-1">
      <ConnectorLogo
        websiteUrl={artifact.websiteUrl}
        name={artifact.providerDisplayName}
        size="lg"
        className="shrink-0 rounded-lg"
        fallback={<Globe className="size-5" />}
      />
      <div className="min-w-0 flex-1">
        <h2 className="text-base font-medium leading-5 break-words">
          Sign in to {artifact.providerDisplayName}
        </h2>
        <p className="mt-1 text-xs text-muted-foreground">{artifact.statusLabel}</p>
      </div>
      {!pagination && onDismiss && (
        <OmniDismissButton
          onDismiss={onDismiss}
          disabled={staging?.isSubmitting}
          statsigName="artifact-biscuit-dismiss"
        />
      )}
    </div>
    <p className="px-1 text-sm text-muted-foreground">
      Sign in or create an account in the browser, then continue your task.
    </p>
    <div className="flex flex-wrap items-center gap-2 px-1 [&:has(>[data-browser-control-slot]:not(:empty))_[data-manual-browser-control]]:hidden">
      <div ref={browserControlRef} data-browser-control-slot className="contents empty:hidden" />
      {actions.map((action) => {
        const staged =
          staging?.decision?.kind === "action" && staging.decision.action.id === action.id;
        return (
          <Button
            key={action.id}
            data-manual-browser-control={action.id === "take-control" ? "" : undefined}
            type="button"
            variant={action.variant === "primary" ? "primary" : "ghostPopover"}
            onClick={() => onAction(action)}
            disabled={disabled}
            aria-pressed={staging ? staged : undefined}
            data-statsig-name={action.statsigName ?? "artifact-biscuit-action"}
            className="max-w-full whitespace-normal"
          >
            {staged && <Check className="size-4 shrink-0" />}
            {action.verboseLabel ?? action.label}
            {action.method === "GET_EXTERNAL" && <ArrowUpRight className="size-4 shrink-0" />}
            {staged && <span className="text-xs">Queued</span>}
          </Button>
        );
      })}
    </div>
    {staging?.decision?.kind === "note" && (
      <p className="px-1 text-xs text-muted-foreground">
        Note added — sent when you finish the queue
      </p>
    )}
    <p className="flex items-start gap-2 px-1 text-xs leading-5 text-muted-foreground">
      <LockKeyhole className="mt-0.5 size-3.5 shrink-0" aria-hidden />
      Your login details stay out of this chat and are never shared with the assistant.
    </p>
    {followUp && <div className="-mb-[7px] flex flex-col">{followUp}</div>}
  </OmniCardChrome>
);
